// Checkbox — the labelled checkbox, decided once (added 2026-09-18, audit
// C-13, batch 15). Before it, thirteen sites across auth and lib repeated
// `<label class="flex items-center gap-2 text-sm"><input type=checkbox
// class="checkbox checkbox-sm">…` with the size drifting between `checkbox`
// (24px) and `checkbox-sm` (20px), no hint or error contract, and the checked
// fill left at daisyUI's default (a transparent box with a body-ink mark)
// where §12.6 says checked = accent 900. Toggle existed as the sibling for a
// setting that takes effect at once; this is the control for a choice the
// form COMMITS on Save (Toggle's own JSDoc sends you here), and for a row's
// selection in a table.
//
// SPECTRUM (spectrum.adobe.com/page/checkbox, the §9 laws in spectrum.css):
//   stroke   2px in both states (checkbox border-width-200, v15.2.0 dist);
//   radius   = border width, 2px — base.css's corner split (object-styles R3:
//            inside square, outside round);
//   rest     gray-600 stroke (--ds-control-border, §9 through daisyUI's own
//            --input-color hook, unchecked-scoped);
//   checked  accent 900 fill + primary-content mark = `checkbox-primary`
//            (DEV-GUIDE-002 §12.3 "checkbox / radio / switch checked");
//   target   the 32px §8 floor grows around the glyph as a centred ::after
//            (stood down inside .table, where DataTable stretches the label);
//   disabled the gray stops through §9's :disabled rule (never opacity) and
//            --ds-text-disabled + cursor-not-allowed on the text (§12.2).
//
// SIZES: `m` (default) = `checkbox-sm`, 20px — the majority rung at the
// thirteen sites and the one that sits on a 14px/1.5 body line; `s` =
// `checkbox-xs`, 16px — Spectrum's L control size and the DataTable's cell
// glyph, for a dense row. daisyUI's bare `checkbox` (24px) is not offered:
// no Spectrum checkbox is that large at desktop scale.
//
// ANATOMY: the <label> wraps ONLY the box and the label text, so the control's
// accessible name is the label alone; hint and error are siblings tied in
// through aria-describedby as `${id}-hint` / `${id}-error` (the Field idiom).
// A hidden label (a table's selection cell, where the column header carries
// the meaning) becomes the input's aria-label — the control is never nameless.
import { Show, createEffect, type Accessor } from "solid-js";
import { CircleAlert } from "lucide-solid";

/** `m` (default) 20px = `checkbox-sm`, the form rung; `s` 16px = `checkbox-xs`, the table-cell rung (Spectrum's L control size). */
export type CheckboxSize = "s" | "m";

export interface CheckboxProps {
  /** Controlled state — a boolean, or an accessor for a caller that already holds one (`checked={selected}`). The caller owns it. */
  checked: boolean | Accessor<boolean>;
  /** Receives the next boolean on click or Space. An indeterminate box reports `true` — the mixed state resolves to "all". */
  onChange: (next: boolean) => void;
  /** The choice, as a sentence the box answers ("Also withdraw all copies"). Visible unless `labelHidden`; the accessible name either way. */
  label: string;
  /** One line under the label saying what ticking it does or costs ("Zeroes the subscription cost"). `${id}-hint`, wired through aria-describedby. */
  hint?: string;
  /** Greys the row (text-base-disabled) and blocks input — for a choice the role cannot make. Say why in `hint`. */
  disabled?: boolean;
  /** Validation message — replaces the hint, sets aria-invalid, `${id}-error` wired through aria-describedby. */
  error?: string;
  /** The "some but not all" state of a parent box (a table's select-all) — the native `indeterminate` property, which AT already reads as mixed; `checked` still decides what a click reports. */
  indeterminate?: boolean;
  /** `m` (default) or `s` — see the header. */
  size?: CheckboxSize;
  /** Automation id on the <input>; the hint and error derive `${id}-hint` / `${id}-error` from it. REQUIRED — every checkbox is a test target. */
  id: string;
  /** Render no visible text: the label becomes the input's aria-label. For a table selection cell where the column names the meaning — pass `label` everywhere else. */
  labelHidden?: boolean;
  /** Extra classes on the OUTERMOST element — layout only (`sm:col-span-2`, `h-full`), never colour or size. */
  class?: string;
}

/** The two daisyUI rungs, literal so Tailwind sees them. */
const BOX: Record<CheckboxSize, string> = {
  s: "checkbox-xs",
  m: "checkbox-sm",
};
/** The top nudge that centres the box on a 21px body line (14px × 1.5) when the label may wrap; a hidden label centres the box on the row instead. */
const NUDGE: Record<CheckboxSize, string> = {
  s: "mt-0.5",
  m: "mt-px",
};
/** Hint / error indent = box width + the 8px gap, so the text aligns under the label. */
const INDENT: Record<CheckboxSize, string> = {
  s: "ps-6",
  m: "ps-7",
};

/**
 * A labelled checkbox for a choice the form commits on Save ("Open access",
 * "Also withdraw all copies") or a row's selection in a table. For a setting
 * that takes effect the moment it flips, use `@erp/ui/toggle`; for one of
 * several values, `@erp/ui/segmented-control` or `@erp/ui/choice-tiles`.
 */
export function Checkbox(props: CheckboxProps) {
  let el!: HTMLInputElement;
  const size = () => props.size ?? "m";
  const checked = () => (typeof props.checked === "function" ? props.checked() : props.checked);
  const hintId = () => `${props.id}-hint`;
  const errorId = () => `${props.id}-error`;
  const describedBy = () => {
    const ids: string[] = [];
    if (props.error) ids.push(errorId());
    else if (props.hint) ids.push(hintId());
    return ids.length ? ids.join(" ") : undefined;
  };
  // `indeterminate` is a DOM property, not an attribute — it can only be set
  // from script, so it rides an effect off the prop.
  createEffect(() => {
    el.indeterminate = !!props.indeterminate;
  });
  return (
    <div class={`flex min-w-0 flex-col gap-1 ${props.class ?? ""}`}>
      <label
        class={`flex min-w-0 gap-2 ${props.labelHidden ? "flex-1 items-center" : "items-start"} ${
          props.disabled ? "cursor-not-allowed" : "cursor-pointer"
        }`}
      >
        <input
          ref={el}
          id={props.id}
          type="checkbox"
          class={`checkbox checkbox-primary shrink-0 ${BOX[size()]} ${props.labelHidden ? "" : NUDGE[size()]}`}
          checked={checked()}
          disabled={props.disabled}
          aria-label={props.labelHidden ? props.label : undefined}
          aria-invalid={props.error ? "true" : undefined}
          aria-describedby={describedBy()}
          onChange={(e) => props.onChange(e.currentTarget.checked)}
        />
        <Show when={!props.labelHidden}>
          <span class={`min-w-0 text-sm ${props.disabled ? "text-base-disabled" : "text-base-content"}`}>{props.label}</span>
        </Show>
      </label>
      {/* Hint and error are Field's help text — font-size-75 in gray-700, an
          alert glyph leading the negative message — indented under the label
          text, outside the <label> so they describe the control rather than
          name it. */}
      <Show
        when={props.error}
        fallback={
          <Show when={props.hint}>
            <p id={hintId()} class={`${INDENT[size()]} text-xs leading-snug ${props.disabled ? "text-base-disabled" : "text-base-muted"}`}>
              {props.hint}
            </p>
          </Show>
        }
      >
        <p id={errorId()} class={`flex items-start gap-[7px] ${INDENT[size()]} text-xs leading-snug text-error`}>
          <CircleAlert size={16} class="mt-[3px] shrink-0" aria-hidden="true" />
          {props.error}
        </p>
      </Show>
    </div>
  );
}
